<template>
    <section class="flex flex-col gap-5">
        <p class="sr-only" role="status">{{ processing ? t("reflections.self.processing") : "" }}</p>
        <Launch360Animation v-if="processing && isSixty" :label="t('reflections.self.processing')" @done="finishAnimation" />
        <ReflectionProcessing v-else-if="processing" />
        <div v-else-if="loading" class="scaffold-body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <!-- Reached by a deep link or stale hub state: a submitted response is
             final, so the taker never renders for one. -->
        <BaseCard v-else-if="alreadySubmitted" padding="lg" class="flex flex-col items-start gap-3">
            <StatusChip state="done" size="2xs">{{ t("reflections.self.taker.complete") }}</StatusChip>
            <div class="body-semibold text-ink">{{ t("reflections.self.taker.submittedOn", { date: formatDate(alreadySubmitted, "long") }) }}</div>
            <p class="scaffold-body-small text-ink-muted">{{ t("reflections.self.taker.answersFinal") }}</p>
            <BaseButton theme="neutral" size="sm" @click="emit('back')">{{ t("reflections.self.taker.backToReflections") }}</BaseButton>
        </BaseCard>

        <template v-else-if="showingIntro && intro">
            <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("components.assessment.back") }}
            </BaseButton>
            <AssessmentIntroLanding :intro="intro" @begin="beginQuestions" />
        </template>

        <template v-else-if="usesPagedRenderer">
            <h2 ref="questionsHeading" tabindex="-1" class="title-subsection text-ink outline-none">{{ title }}</h2>
            <div v-if="closes" class="scaffold-details-small text-ink-muted -mt-3">{{ closes }}</div>
            <AssessmentGroupedSections
                ref="groupedSections"
                :response-id="responseId"
                :questions="questions"
                :sections="sections"
                :display-mode="displayMode"
                :questions-per-page="questionsPerPage"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :accent-color="accentColor"
                :accent-surface-color="accentSurfaceColor"
                @submitted="onGroupedSubmitted"
                @exit="emit('back')" />
        </template>

        <template v-else-if="currentQuestion">
            <header class="flex flex-col gap-3">
                <div class="flex items-center gap-3 flex-wrap">
                    <h2 ref="questionsHeading" tabindex="-1" class="title-subsection text-ink outline-none">{{ title }}</h2>
                    <BaseChip v-if="stepOf" size="2xs">{{ t("reflections.hub.step", stepOf) }}</BaseChip>
                </div>
                <div class="flex flex-col gap-2">
                    <BaseProgressBar :value="progressPct" size="sm" :aria-label="t('components.assessment.progress', { percent: progressPct })" :color="accentColor" />
                    <div class="scaffold-details-small text-ink-muted">
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- pure punctuation separator between two values -->
                        {{ headerStatus }}<template v-if="closes"> · {{ closes }}</template>
                    </div>
                </div>
            </header>

            <QuestionCard
                :question="currentQuestion"
                :score="currentSavedScore"
                :text="currentTextDraft"
                :not-observed="currentSavedNotObserved"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isCurrentUnanswered"
                :accent-color="accentColor"
                :accent-surface-color="accentSurfaceColor"
                @update:score="setScore"
                @update:text="currentTextDraft = $event"
                @update:not-observed="setNotObserved"
                @update:busy="composerBusy = $event" />

            <BaseNotification v-if="error" theme="warning" :text="error" hide-dismiss />

            <div class="flex items-center justify-between">
                <BaseButton type="button" :disabled="currentIndex === 0 || saving || composerBusy" theme="tertiary" @click="onBack">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("components.assessment.back") }}
                </BaseButton>

                <BaseButton v-if="!isLast" theme="primary" :disabled="saving || composerBusy" @click="goNext"> {{ t("components.assessment.next") }} </BaseButton>
                <BaseButton v-else theme="primary" :disabled="submitting || saving || composerBusy" @click="submit">
                    {{ submitting ? t("components.assessment.submitting") : t("components.assessment.submit") }}
                </BaseButton>
            </div>
            <div v-if="composerBusy" class="scaffold-details-small text-ink-muted text-right -mt-3">{{ t("components.assessment.stopRecording") }}</div>
        </template>

        <!-- A submitted response is never re-filled, but staff can still create a fresh one to demo. -->
        <AssessmentDemoTools
            v-if="responseId"
            :response-actions="alreadySubmitted ? [] : [{ id: responseId, label: title }]"
            :bootstrap-kind="isSixty ? 'sixty' : 'self'"
            :before-fill="saveDrafts"
            @filled="onFilled" />

        <SubmitConfirmDialog v-model:open="showSubmitConfirm" :title="t('components.assessment.submitConfirmTitle')" :confirm-disabled="submitting" @confirm="confirmSubmit">
            <p class="body text-ink-muted">
                {{ closesDate ? t("reflections.submitConfirm.bodyUntil", { date: closesDate }) : t("reflections.submitConfirm.bodyOpen") }}
            </p>
        </SubmitConfirmDialog>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, AssessmentIntro, QuestionResponseData, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import AssessmentIntroLanding from "~vue/components/AssessmentIntroLanding.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import Launch360Animation from "~vue/pages/reflections/Launch360Animation.vue";
import ReflectionProcessing from "~vue/pages/reflections/ReflectionProcessing.vue";
import SubmitConfirmDialog from "~vue/pages/reflections/SubmitConfirmDialog.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { DEFAULT_QUESTIONS_PER_PAGE, rendersQuestionPages } from "~vue/utils/assessmentSections";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { logUserInteraction } from "~vue/utils/logUtils";
import { closesLabel, formatDate, isNetworkError } from "~vue/utils/threeSixty";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    cycleId: string;
    title?: string;
    accentColor?: string;
    accentSurfaceColor?: string;
    // Set when the taker is the last leg of the 360 setup flow, so the header
    // can carry the same step marker as the setup and nomination screens.
    stepOf?: { current: number; total: number };
    // The self leg of a 360 keeps the cycle's identity through submission, so it
    // resolves into the pink 360 mark rather than the self-reflection bowl.
    isSixty?: boolean;
    // The cycle's deadline, so the subject knows how long they have to finish.
    // Null for a DRAFT cycle that hasn't been launched yet.
    closesAt?: string | null;
}

const {
    cycleId,
    title: titleProp,
    accentColor = "var(--color-status-warning)",
    accentSurfaceColor = "var(--color-chip-self-reflect-bg)",
    stepOf,
    isSixty = false,
    closesAt = null,
} = defineProps<Props>();

const { t } = useI18n();
const title = computed(() => titleProp ?? t("reflections.self.title"));

const emit = defineEmits<{
    (event: "submitted"): void;
    (event: "back"): void;
}>();

const questions = ref<QuestionWithSaved[]>([]);
const sections = ref<SectionSummary[]>([]);
const responseId = ref<string>("");
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const questionsPerPage = ref(DEFAULT_QUESTIONS_PER_PAGE);
const intro = ref<AssessmentIntro | null>(null);
const showingIntro = ref(false);
const usesPagedRenderer = computed(() => rendersQuestionPages(displayMode.value));
const currentIndex = ref(0);
const currentTextDraft = ref<string>("");
const groupedSections = useTemplateRef<InstanceType<typeof AssessmentGroupedSections>>("groupedSections");
const questionsHeading = useTemplateRef<HTMLElement>("questionsHeading");
const loading = ref(true);
const loadError = ref<string | null>(null);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const showSubmitConfirm = ref(false);
const showValidation = ref(false);
// Read from the load payload only, never from this session's own submit, so
// finishing the taker doesn't flash the read-only state on the way out.
const alreadySubmitted = ref<string | null>(null);
// The processing animation plays after a successful submit for at least
// PROCESSING_MIN_MS so the transition feels grounded even when the API returns
// instantly. If the API takes longer, we hold the animation until it resolves —
// no clipped illustration on slow networks. The 360 mark is a one-shot sequence,
// so it reports its own end rather than being timed.
const processing = ref(false);
const PROCESSING_MIN_MS = 2400;
// The 360 mark's sequence reports done at ~2.9s. The response is already
// committed by the time it plays, so a mark that never reports — unmounted
// mid-sequence, timers cleared — must not strand the user on the processing
// screen: the wait is capped well past the sequence's own length.
const ANIMATION_TIMEOUT_MS = 6000;
const composerBusy = ref(false);
let animationDone = Promise.resolve();
let resolveAnimation: (() => void) | null = null;
let animationTimer: ReturnType<typeof setTimeout> | null = null;

function finishAnimation() {
    if (animationTimer !== null) {
        clearTimeout(animationTimer);
        animationTimer = null;
    }
    resolveAnimation?.();
    resolveAnimation = null;
}

function startProcessing() {
    if (isSixty) {
        animationDone = new Promise<void>((resolve) => {
            resolveAnimation = resolve;
            animationTimer = setTimeout(finishAnimation, ANIMATION_TIMEOUT_MS);
        });
    }
    processing.value = true;
}

onBeforeUnmount(finishAnimation);

async function waitForProcessing(startedAt: number) {
    if (isSixty) {
        await animationDone;
        return;
    }
    const remaining = PROCESSING_MIN_MS - (Date.now() - startedAt);
    if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
    }
}

function answeredCount(): number {
    return questions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
}

function logSubmitted() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_SUBMITTED, {
        cycle_id: cycleId,
        total_questions: questions.value.length,
        answered_count: answeredCount(),
    });
}

onMounted(async () => {
    const { data, error: fetchError, response } = await useFetch(`/v1/reflections/cycles/${cycleId}/self-response`).post().json();
    loading.value = false;
    if (fetchError.value) {
        if (response.value?.status === 410) {
            loadError.value = t("reflections.self.taker.closed");
        } else {
            loadError.value = (data.value as { error?: string })?.error || t("reflections.self.taker.loadFailed");
        }
        return;
    }
    const payload = (data.value as { response: ResponseWithQuestions }).response;
    questions.value = payload.questions;
    sections.value = payload.sections ?? [];
    responseId.value = payload.id;
    allowScoreExamples.value = payload.allow_score_examples ?? true;
    allowNotObserved.value = payload.allow_not_observed ?? true;
    displayMode.value = payload.display_mode ?? "one_per_page";
    questionsPerPage.value = payload.questions_per_page ?? DEFAULT_QUESTIONS_PER_PAGE;
    alreadySubmitted.value = payload.submitted_at ?? null;
    intro.value = payload.intro ?? null;
    showingIntro.value = intro.value !== null && !payload.submitted_at;
    currentTextDraft.value = payload.questions[0]?.saved.text ?? "";
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_VIEWED, {
        cycle_id: cycleId,
        total_questions: payload.questions.length,
        answered_count: answeredCount(),
    });
});

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const currentSavedScore = computed(() => currentQuestion.value?.saved.score ?? null);
const currentSavedNotObserved = computed(() => currentQuestion.value?.saved.not_observed ?? false);
const isLast = computed(() => currentIndex.value === questions.value.length - 1);
const progressPct = computed(() => Math.round(((currentIndex.value + 1) / questions.value.length) * 100));

const headerStatus = computed(() => t("reflections.self.taker.questionOf", { current: currentIndex.value + 1, total: questions.value.length }));

const closes = computed(() => closesLabel(closesAt));
const closesDate = computed(() => formatDate(closesAt, "long"));

const isCurrentUnanswered = computed(() => {
    if (!currentQuestion.value) {
        return false;
    }
    return isRequiredUnanswered(currentQuestion.value);
});

// Begin unmounts the focused button, so hand focus to the questions or it
// falls back to the document and the next Tab starts at the hub's controls.
async function beginQuestions() {
    showingIntro.value = false;
    await nextTick();
    questionsHeading.value?.focus();
}

async function saveDrafts(): Promise<boolean> {
    // Nothing has been answered behind the intro, and saving the first
    // question from here would post an empty answer for it.
    if (showingIntro.value) {
        return true;
    }
    if (usesPagedRenderer.value) {
        return (await groupedSections.value?.saveDirtyOnPage()) ?? true;
    }
    return saveCurrent();
}

async function onFilled(filled: QuestionResponseData[]) {
    if (showingIntro.value) {
        showingIntro.value = false;
        // The grouped renderer only mounts once the intro is gone.
        await nextTick();
    }
    const byQuestionId = new Map(filled.map((answer) => [answer.question_id, answer]));
    for (const question of questions.value) {
        const answer = byQuestionId.get(question.id);
        if (answer) {
            question.saved = { score: answer.score ?? null, text: answer.text, not_observed: answer.not_observed };
        }
    }
    groupedSections.value?.adoptSavedAnswers([...byQuestionId.keys()]);
    // Land on the last step so the filled assessment is one click from Submit.
    groupedSections.value?.goToLastPage();
    currentIndex.value = Math.max(questions.value.length - 1, 0);
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showValidation.value = false;
    error.value = null;
}

function setScore(n: number) {
    if (!currentQuestion.value) {
        return;
    }
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score: n, not_observed: false };
}

function setNotObserved(v: boolean) {
    if (!currentQuestion.value) {
        return;
    }
    const score = v ? null : currentQuestion.value.saved.score;
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score, not_observed: v };
}

async function saveCurrent(): Promise<boolean> {
    if (!currentQuestion.value) {
        return true;
    }
    if (currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers`)
            .post(
                JSON.stringify({
                    question_id: currentQuestion.value.id,
                    score: currentQuestion.value.saved.score,
                    text: currentQuestion.value.saved.text,
                    not_observed: currentQuestion.value.saved.not_observed,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = t("components.assessment.saveFailed");
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

function validateCurrentQuestion(action: "continuing" | "submitting"): boolean {
    if (currentQuestion.value && currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    if (currentQuestion.value && isRequiredUnanswered(currentQuestion.value)) {
        showValidation.value = true;
        error.value = action === "continuing" ? t("reflections.self.taker.requiredBeforeContinuing") : t("reflections.self.taker.requiredBeforeSubmitting");
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (currentIndex.value >= questions.value.length - 1) {
        return;
    }
    if (!validateCurrentQuestion("continuing")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value += 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
}

async function onBack() {
    // On the first question, "Back" exits the take flow entirely (returns to
    // the hub's list view). Later questions step backward through the flow.
    if (currentIndex.value === 0) {
        emit("back");
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value -= 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
}

async function submit() {
    if (submitting.value) {
        return;
    }
    if (!validateCurrentQuestion("submitting")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    // Hide the confirm dialog and swap the questions view for the processing
    // animation as soon as the request goes out — that way the user always
    // sees the transition, not a flash of the taker UI.
    showSubmitConfirm.value = false;
    startProcessing();
    const started = Date.now();
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit`).post().json();
        if (fetchError.value) {
            error.value = isNetworkError(fetchError.value)
                ? t("components.assessment.connectionSubmitFailed")
                : (data.value as { error?: string })?.error || t("components.assessment.submitFailed");
            processing.value = false;
            return;
        }
        logSubmitted();
        await waitForProcessing(started);
        emit("submitted");
    } finally {
        submitting.value = false;
    }
}

// The grouped renderer owns its own save/submit, so the processing animation
// runs for its full duration after the response has already been submitted.
async function onGroupedSubmitted() {
    logSubmitted();
    startProcessing();
    await waitForProcessing(Date.now());
    emit("submitted");
}
</script>
